import React, { useEffect, useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import { Question } from "../../Types/Question.types";
import GeneratorQuestion from "./GeneratorQuestion";
import styles from "./GeneratorForm.module.css";
import FormInput from "../../uiComponents/Form/FormInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faDownload,
  faLightbulb,
  faPlayCircle,
} from "@fortawesome/free-solid-svg-icons";
import { colorPallette } from "../../Common/theme";
import { checkDependencies } from "../../Utils/FormMethods";
import { setAppError, setLoading } from "../../Utils/modalMethods";
import { getModifiableQuestions } from "../../Utils/questionTypeProcessor";
import { Control } from "react-hook-form";
import { useAuth0 } from "@auth0/auth0-react";
import { generateAndDownloadWord } from "../../Utils/wordGeneration";
import { APIGetWithError, APIPostFile } from "../../Services/authenticated";

type Props = {
  control: Control<any>;
  watch: any;
  formWatch: any;
  setValue: any;
  unregister: any;
  handleSubmit: any;
  setIsReview: React.Dispatch<React.SetStateAction<boolean>>;
  lsSections?: string[];
  documentKind: string;
};

const ModifyableReviewSection: React.FC<Props> = ({
  control,
  watch,
  formWatch,
  setValue,
  unregister,
  handleSubmit,
  documentKind,
  setIsReview,
}) => {
  const [documentQuestions, setDocumentQuestions] = useState<Question[]>([]);
  const [companyId, setCompanyId] = useState<string | undefined>();
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const formData = localStorage.getItem("generator-form-data");
    if (formData) {
      const parsed = JSON.parse(formData);
      setDocumentQuestions(parsed.questions);
    } else {
      setAppError();
    }
  }, []);

  useEffect(() => {
    const cleanupInvalidFields = () => {
      Object.keys(formWatch).forEach((key) => {
        const question = documentQuestions.find(
          (q) => String(q.id) === String(key)
        );
        if (question && !checkDependencies(question, formWatch)) {
          unregister(key);
        }
      });
    };
    cleanupInvalidFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formWatch]);

  useEffect(() => {
    const fetchCompany = async () => {
      setCompanyId(undefined);
      try {
        const token = await getAccessTokenSilently();
        const company = await APIGetWithError("/companies/get-company", token);
        setCompanyId(company?.companyId || "ERROR");
      } catch {
        setCompanyId("ERROR");
      }
    };
    fetchCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPrevious = () => {
    setIsReview(false);
    window.scrollTo(0, 0);
  };

  const saveDocument = async (
    file: any,
    docName: string,
    documentKind: string
  ) => {
    const now = Date.now();
    try {
      const token = await getAccessTokenSilently();
      const params = {
        data: file,
        docName: `${docName}_${now}.docx`,
        documentType: "Word",
        fileNameNoExtension: docName,
        versionId: now,
        versionNumber: 1,
        date: now,
        size: file.size,
        updateDate: now,
        format: "word",
        documentKind,
        andesGenerated: true,
      };

      const docRef = await APIPostFile(
        "/doc-ref/create-doc-ref",
        token,
        params
      );

      if (!docRef.documentSaved) {
        setAppError(
          "No se pudo guardar el documento en la nuve",
          "El documento se descargo en su computadora pero no se pudo guardar en la nube"
        );
      }
    } catch {
      setAppError(
        "No se pudo guardar el documento en la nuve",
        "El documento se descargo en su computadora pero no se pudo guardar en la nube"
      );
    }
  };

  const onDownload = async () => {
    const isInternalTesting = window.location.href.includes("/testing");
    const docDataRaw = localStorage.getItem("generator-form-document");

    if (!docDataRaw) {
      setAppError(
        "Aun no se agrego el documento",
        "Primero debe crear un documento para poder generarlo"
      );
      return;
    }

    const parsedData = JSON.parse(docDataRaw);

    console.log("Parsed Data:", parsedData);

    if (isInternalTesting) {
      const invalid =
        !parsedData.document ||
        !parsedData.document[0] ||
        !parsedData.document[0].text;

      if (invalid) {
        setAppError(
          "Aun no se agrego el documento",
          "Primero debe crear un documento para poder generarlo"
        );
        return;
      }

      return generateAndDownloadWord(
        parsedData,
        companyId,
        `Testing-${formWatch?.documentName || "Document"}`
      );
    }

    try {
      setLoading(true);
      if (!companyId || companyId === "ERROR") {
        if (companyId === "ERROR") {
          // re-fetch
          const token = await getAccessTokenSilently();
          const company = await APIGetWithError(
            "/companies/get-company",
            token
          );
          setCompanyId(company?.companyId || "ERROR");
        }
        return;
      }

      window.scrollTo(0, 0);
      const file = await generateAndDownloadWord(
        parsedData,
        companyId,
        formWatch?.documentName || "Documento Andes Docs"
      );
      await saveDocument(
        file,
        formWatch?.documentName || "Documento Andes Docs",
        documentKind
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data: any) => {
    const prevAnswers = JSON.parse(
      localStorage.getItem("generator-form-answers") || "{}"
    );

    const currentSectionQuestionIds = getModifiableQuestions(
      documentQuestions
    ).map((question) => String(question.id));

    const filteredPrevData = Object.fromEntries(
      Object.entries(prevAnswers).filter(
        ([key]) => !currentSectionQuestionIds.includes(String(key))
      )
    );

    localStorage.setItem(
      "generator-form-answers",
      JSON.stringify({ ...filteredPrevData, ...data })
    );

    onDownload();
  };

  return (
    <Box width={"100%"}>
      <Stack minHeight="100vh" alignItems="center" className="b-tertiary" p={4}>
        <Stack
          direction="row"
          width="100%"
          textAlign="left"
          maxWidth={1000}
          my={5}
          pl={2}
          fontWeight="Bold"
          fontSize={23}
          alignItems="center"
        >
          <FontAwesomeIcon
            icon={faPlayCircle}
            style={{
              color: colorPallette.darkSecondary,
              marginRight: 10,
            }}
          />
          <Stack sx={{ transform: "translateY(-8px)" }}>
            <Box fontWeight={400} fontSize={14} mb={"-2px"}>
              Sección final
            </Box>
            Descarga Multiple de Documento
          </Stack>
        </Stack>
        <Box maxWidth={1000} mb={1} width="100%" textAlign="left" fontSize={16}>
          <FontAwesomeIcon
            icon={faLightbulb}
            style={{ color: colorPallette.primary, marginRight: "4px" }}
          />{" "}
          Poedés modificar los datos y volver a descargar el documento todas las
          veces que necesites.
        </Box>
        <Box maxWidth={1000} mb={1} width="100%" textAlign="left" fontSize={16}>
          <FontAwesomeIcon
            icon={faLightbulb}
            style={{ color: colorPallette.primary, marginRight: "4px" }}
          />{" "}
          Utilizá esta sección cuando tengas que generar muchos documentos
          similares con solo algunos datos diferentes.
        </Box>
        <Box maxWidth={1000} mb={4} width="100%" textAlign="left" fontSize={16}>
          <FontAwesomeIcon
            icon={faLightbulb}
            style={{ color: colorPallette.primary, marginRight: "4px" }}
          />{" "}
          Si necesitas modificar algun dato anterior podes volver atras y
          regresar
        </Box>
        <Box className={styles.card} maxWidth="1000px">
          <FormInput
            label="Nombre del documento:"
            name="documentName"
            control={control}
            required
            info={[
              "Este va a ser el nombre del documento que se va a generar",
              "Por ejemplo: Contrato de alquiler Ricardo Darín",
            ]}
          />
        </Box>
        {getModifiableQuestions(documentQuestions).map((question) => (
          <GeneratorQuestion
            key={question.id}
            question={question}
            control={control}
            watch={watch}
            setValue={setValue}
          />
        ))}
        <Stack
          direction="row"
          width="100%"
          maxWidth="1000px"
          justifyContent="space-between"
          my={2}
          mb={8}
        >
          <Button
            onClick={onPrevious}
            sx={{
              mr: 2,
              color: colorPallette.lightRed,
            }}
          >
            <Box component="span" mr={1}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </Box>
            Anterior
          </Button>
          <Button
            onClick={handleSubmit(onSubmit, () =>
              setAppError(
                "Debe completar todos los campos",
                "Revise los campos marcados en rojo"
              )
            )}
            sx={{ width: 350 }}
            variant="contained"
          >
            <Box component="span" mr={1}>
              <FontAwesomeIcon icon={faDownload} />
            </Box>
            Descargar documento con datos actuales
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ModifyableReviewSection;
